import { Link } from "react-router-dom";
import { IconRise, IconScore, IconArrowRise } from "@/components/landing/icons";
import SectionMark from "@/components/landing/SectionMark";
import Reveal from "@/components/landing/Reveal";
import { SCORE_BANDS, WHAT_IT_CHECKS, HOW_ATS_WORKS, ATS_FAQ } from "@/lib/ats-content";

/**
 * The content half of the ATS resume checker page.
 *
 * Sits BELOW the tool, deliberately. Somebody arriving from a search for "free
 * ATS resume checker" wants the checker, and burying it under an essay to feed
 * a crawler trades a real conversion for a hypothetical ranking. The content is
 * in the HTML either way — crawlers do not care that it is below the fold, and
 * people who want the explanation scroll.
 */
const TONE = {
  verified: "border-verified/40 bg-verified/5 text-verified",
  flag: "border-flag/40 bg-flag/5 text-flag",
  low: "border-rose-300 bg-rose-50 text-rose-700",
};

export default function AtsExplainer() {
  return (
    <>
      <section className="border-t border-sand bg-paper" id="how-ats-works">
        <div className="max-w-7xl mx-auto px-6 py-12 lg:py-16">
          <div className="max-w-2xl">
            <SectionMark index={1} total={4}>How an ATS works</SectionMark>
            <h2 className="mt-4 font-heading text-3xl lg:text-4xl font-bold tracking-tight text-stone-950">
              It is a search box, not a <em className="font-display not-italic text-dawn-800">judge</em>.
            </h2>
          </div>
          <div className="mt-10 grid lg:grid-cols-3 gap-5">
            {HOW_ATS_WORKS.map(([title, body], i) => (
              <Reveal key={title} delay={i * 60}>
                <div className="h-full p-6 border border-sand rounded-sm bg-white shadow-[var(--shadow-card)]">
                  <span className="font-mono text-[11px] text-stone-600">0{i + 1}</span>
                  <h3 className="mt-2 font-heading text-lg font-bold tracking-tight text-stone-950">{title}</h3>
                  <p className="mt-2 text-sm text-stone-600 leading-relaxed">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          {/* The claim every competing page opens with, and why it is not here.
              Saying so is better content than repeating it would be. */}
          <p className="mt-8 text-sm text-stone-600 leading-relaxed max-w-3xl border-l-2 border-sand pl-4">
            You will read that some large percentage of resumes are rejected by software before a
            human sees them. That figure traces back to no primary source, so it is not on this
            page. The real, checkable problem is narrower and more fixable: your resume has to
            surface in the search a recruiter actually runs.
          </p>
        </div>
      </section>

      <section className="border-t border-sand" id="score-meaning">
        <div className="max-w-7xl mx-auto px-6 py-12 lg:py-16">
          <div className="max-w-2xl">
            <SectionMark index={2} total={4}>Reading the score</SectionMark>
            <h2 className="mt-4 font-heading text-3xl lg:text-4xl font-bold tracking-tight text-stone-950">
              What your number actually means.
            </h2>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {SCORE_BANDS.map((b) => (
              <div key={b.band} className="p-6 border border-sand rounded-sm bg-white shadow-[var(--shadow-card)]">
                <div className="flex items-baseline gap-3">
                  <span className="font-heading text-3xl font-bold tracking-tighter text-stone-950 tabular-nums">
                    {b.band}
                  </span>
                  <span className={`text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 rounded-sm border ${TONE[b.tone]}`}>
                    {b.verdict}
                  </span>
                </div>
                <p className="mt-3 text-sm text-stone-600 leading-relaxed">{b.meaning}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-sand bg-paper" id="what-we-check">
        <div className="max-w-7xl mx-auto px-6 py-12 lg:py-16 grid lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-4">
            <SectionMark index={3} total={4}>What it checks</SectionMark>
            <h2 className="mt-4 font-heading text-3xl lg:text-4xl font-bold tracking-tight text-stone-950">
              Six answers, not one number.
            </h2>
            <p className="mt-4 text-stone-600 leading-relaxed">
              A score on its own tells you that something is wrong and not what. These are the
              fields the analysis returns, so you know which sentence to change.
            </p>
            <Link to="/features" className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:underline underline-offset-4">
              See everything NextOpp does <IconArrowRise className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
            {WHAT_IT_CHECKS.map(([title, body]) => (
              <div key={title} className="p-5 border border-sand rounded-sm bg-white shadow-[var(--shadow-card)]">
                <div className="flex items-start gap-2.5">
                  <IconScore className="w-[17px] h-[17px] mt-0.5 text-brand shrink-0" />
                  <div>
                    <h3 className="font-heading text-base font-bold tracking-tight text-stone-950">{title}</h3>
                    <p className="mt-1.5 text-sm text-stone-600 leading-relaxed">{body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-sand" id="ats-faq">
        <div className="max-w-7xl mx-auto px-6 py-12 lg:py-16 grid lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-4">
            <SectionMark index={4} total={4}>Questions</SectionMark>
            <h2 className="mt-4 font-heading text-3xl lg:text-4xl font-bold tracking-tight text-stone-950">
              Asked and answered.
            </h2>
            <p className="mt-4 text-stone-600 leading-relaxed">
              More on how the tailoring works, your data, and pricing is on the{" "}
              <Link to="/faq" className="text-brand hover:underline underline-offset-4">full FAQ</Link>.
            </p>
          </div>
          {/* Real <dl>, not accordions: the answers are the reason the page
              ranks, and content behind a click is content a summarizer has to
              guess at. */}
          <dl className="lg:col-span-8 divide-y divide-sand border-t border-sand">
            {ATS_FAQ.map(([q, a]) => (
              <div key={q} className="py-5">
                <dt className="flex gap-2.5 font-heading text-base font-bold tracking-tight text-stone-950">
                  <IconRise className="w-4 h-4 mt-1 text-verified shrink-0" />
                  {q}
                </dt>
                <dd className="mt-2 ml-[26px] text-sm text-stone-600 leading-relaxed">{a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
    </>
  );
}
